import React, { Fragment, useState, useEffect, useCallback, useRef } from 'react';
import { useTable, useSortBy, useFilters, useGlobalFilter, useAsyncDebounce, usePagination, useRowSelect } from 'react-table';
import Style from './rtable.module.scss';

const IndeterminateCheckbox = React.forwardRef(({ indeterminate, ...rest }, ref) => {
  const defaultRef = React.useRef();
  const resolvedRef = ref || defaultRef;
  React.useEffect(() => { resolvedRef.current.indeterminate = indeterminate; }, [resolvedRef, indeterminate]);
  return <input type="checkbox" ref={resolvedRef} {...rest} />;
});

function GlobalFilter({ preGlobalFilteredRows, globalFilter, setGlobalFilter }) {
  const [value, setValue] = React.useState(globalFilter);
  const onChange = useAsyncDebounce(v => setGlobalFilter(v || undefined), 200);
  return (
    <span>
      <input
        value={value || ""}
        onChange={e => { setValue(e.target.value); onChange(e.target.value); }}
        placeholder="Buscar por id, nombre o mail"
        style={{ fontSize: '1.5rem', height: '2.5em', width: '300px', paddingLeft: '10px' }}
        className={ Style.seeker }
      />
    </span>
  );
}

function Table({ columns, data, setListId }) {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    rows,
    state,
    setGlobalFilter,
    preGlobalFilteredRows,
    visibleColumns,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    selectedFlatRows,
    state: { pageIndex, pageSize }
  } = useTable(
    { columns, data },
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect,
    hooks => {
      hooks.visibleColumns.push(cols => [
        {
          id: 'selection',
          Header: ({ getToggleAllPageRowsSelectedProps }) => (
            <div><IndeterminateCheckbox {...getToggleAllPageRowsSelectedProps()} /></div>
          ),
          Cell: ({ row }) => (
            <div><IndeterminateCheckbox {...row.getToggleRowSelectedProps()} /></div>
          ),
        },
        ...cols,
      ]);
    }
  );

  const selSig = React.useMemo(() => selectedFlatRows.map(r => r.id).join('|'), [selectedFlatRows]);
  const lastSigRef = useRef('');
  useEffect(() => {
    if (selSig !== lastSigRef.current) {
      lastSigRef.current = selSig;
      const selectedRows = selectedFlatRows.map(r => r.original);
      setListId(selectedRows);
    }
  }, [selSig, selectedFlatRows, setListId]);

  return (
    <>
      <div className={ Style.tableWrap }>
        <GlobalFilter preGlobalFilteredRows={preGlobalFilteredRows} globalFilter={state.globalFilter} setGlobalFilter={setGlobalFilter} />
        <table {...getTableProps()} className={ Style.rtable } style={{ marginTop: '2em' }}>
          <thead>
            {headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map(column => (
                  <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                    {column.render('Header')}
                    <span>{column.isSorted ? (column.isSortedDesc ? ' 🔽' : ' 🔼') : ''}</span>
                  </th>
                ))}
              </tr>
            ))}
            <tr><th colSpan={visibleColumns.length} style={{ textAlign: 'left' }}></th></tr>
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map((row) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()}>
                  {row.cells.map(cell => (<td {...cell.getCellProps()}>{cell.render('Cell')}</td>))}
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className={ Style.pagination }>
          <button className={ Style.arrow } onClick={() => gotoPage(0)} disabled={!canPreviousPage}>{'<<'}</button>{' '}
          <button className={ Style.arrow } onClick={() => previousPage()} disabled={!canPreviousPage}>{'<'}</button>{' '}
          <button className={ Style.arrow } onClick={() => nextPage()} disabled={!canNextPage}>{'>'}</button>{' '}
          <button className={ Style.arrow } onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>{'>>'}</button>{' '}
          <span> Página <strong>{pageIndex + 1} de {pageOptions.length}</strong>{' '}</span>
          <span>
            | Filtrar por página:{' '}
            <input
              type="number"
              defaultValue={pageIndex + 1}
              onChange={e => { const p = e.target.value ? Number(e.target.value) - 1 : 0; gotoPage(p); }}
              style={{ width: '50px', marginLeft: '10px', border: '1px solid var(--df-border)' }}
            />
          </span>{' '}
          <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); }}>
            {[10, 20, 30, 40, 50, 100, 500, 1000, 5000].map(ps => (<option key={ps} value={ps}>Mostrar {ps}</option>))}
          </select>
        </div>
      </div>
    </>
  );
}

export function RTable(props){
  const openDetail = (row) => {
    const bg = props.bgMap || {};
    const full = String(row[props.nameField] || row.nombre || row.name || '').trim();
    const parts = full.split(/\s+/).filter(Boolean);
    const first = parts[0] || '';
    const last = parts.length > 1 ? parts[parts.length - 1] : '';
    const second = parts[1] || '';
    const candidates = [full, [first, last].filter(Boolean).join(' '), [first, second].filter(Boolean).join(' '), first].filter(Boolean);
    let info = null;
    for (const k of candidates) { if (bg[k]) { info = bg[k]; break; } }
    const raw = info && (info.raw || null);
    const dw = (info && (info.datawebs_results || null)) || (raw && raw.datawebs_results) || {};
    const score = info && (info.confidence_score ?? info.confidenceScore ?? raw?.confidence_score ?? null);

    const toStringish = v => {
      if (v === undefined || v === null) return 'No se encontró información para este campo';
      if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return String(v);
      return JSON.stringify(v);
    };

    const values = [
      { label: 'Puntaje de confianza', value: score ?? 'No se encontró información para este campo' },
      { label: 'Impedimentos de salida', value: toStringish(dw?.migracion_dataweb?.scrapping_result?.content) },
      { label: 'Obligaciones tributarias', value: toStringish(dw?.sri_dataweb?.scrapping_result?.content) },
      { label: 'Antecedentes', value: toStringish(dw?.funcion_judicial_dataweb?.scrapping_result?.content) },
      { label: 'Procesos', value: toStringish(dw?.fiscalia_dataweb?.scrapping_result?.content) },
      { label: 'Ecuador Legal', value: toStringish(dw?.ecuador_legal_dataweb?.scrapping_result?.content) },
      { label: 'Supercias', value: toStringish(dw?.supercias_dataweb?.scrapping_result?.content) },
      { label: 'Senescyt', value: toStringish(dw?.senescyt_dataweb?.scrapping_result?.content) }
    ];

    const html = `
      <html><head><meta charset="utf-8"/><title>${full}</title></head>
      <body style="font-family:Arial,Helvetica,sans-serif;padding:16px;line-height:1.4">
        <h2 style="margin-top:0">${full}</h2>
        <div style="margin:8px 0"><strong>Puntaje de confianza:</strong> ${score ?? 'No se encontró información para este campo'}</div>
        <hr/>
        ${values.slice(1).map(v => `
          <div style="margin:10px 0">
            <div style="font-weight:bold">${v.label}</div>
            <pre style="white-space:pre-wrap;background:var(--df-surface-raised);border:1px solid var(--df-border);padding:8px;border-radius:6px;margin:4px 0">${toStringish(v.value)}</pre>
          </div>
        `).join('')}
      </body></html>
    `;
    const w = window.open('', '_blank', 'width=720,height=800,scrollbars=yes,resizable=yes');
    if (w) { w.document.open(); w.document.write(html); w.document.close(); }
  };

  const columns = Object.keys(props.data[0])
    .filter(val => val !== '__v')
    .map(val => {
      if (val === props.nameField) {
        return {
          Header: val.charAt(0).toUpperCase() + val.slice(1).split('_')[0] + ' ' + (val.slice(1).split('_')[1] ? val.slice(1).split('_')[1] : '') + ' ' + (val.slice(1).split('_')[2] ? val.slice(1).split('_')[2] : ''),
          accessor: val,
          Cell: ({ value, row }) => (
            <a href="#" onClick={e => { e.preventDefault(); openDetail(row.original); }} style={{ color: 'var(--df-primary)', textDecoration: 'underline', cursor: 'pointer' }}>
              {value || ''}
            </a>
          )
        };
      }
      return {
        Header: val.charAt(0).toUpperCase() + val.slice(1).split('_')[0] + ' ' + (val.slice(1).split('_')[1] ? val.slice(1).split('_')[1] : '') + ' ' + (val.slice(1).split('_')[2] ? val.slice(1).split('_')[2] : ''),
        accessor: val
      };
    });

  let data = props.data;
  data.map(val => {
    let aux = val;
    for (let key in aux) { if (!aux[key]) { aux[key] = ''; } }
  });

  const handleSelection = useCallback((selectedRows) => {
    if (props.createLeadsList) props.createLeadsList(Array.isArray(selectedRows) ? selectedRows : []);
  }, [props.createLeadsList]);

  return (
    <Fragment>
      <Table columns={columns} data={data} setListId={handleSelection} />
    </Fragment>
  );
}
